import { useCallback, useContext, useState } from 'react';
import { useSnackbar } from './useSnackbar';
import {
  SimulatorV2HttpService,
  TSimulatorV2Request,
  TSimulatorV2Response,
} from 'app/services/simulatorHttpServiceV2';
import { ApplicationRequestContext } from 'modules/products/pages/ApplicationRequest/ApplicationRequest';
import { formatCurrency } from 'app/utils/normalizer';
import { getApiErrorMessage, type ApiErrorBody } from 'app/utils/translators';

const useCreditSimulatorV2 = () => {
  const { showSnackbar } = useSnackbar();
  const { elegibilities, setElegibilityError } = useContext(
    ApplicationRequestContext,
  );
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [firstPaymentDateError, setFirstPaymentDateError] = useState(false);

  const validateElegibility = useCallback(
    (data: TSimulatorV2Request): boolean => {
      // Validar campos de elegibilidade
      const installments = data.numPayments;
      const amountValue = data.requestedAmount;
      const interestRate = data.interestRate;

      if (
        installments &&
        elegibilities?.installments?.max &&
        elegibilities?.installments?.min &&
        (installments > elegibilities?.installments.max ||
          installments < elegibilities?.installments.min)
      ) {
        setElegibilityError({
          message: 'Parcelas inválidas',
          description: `O número de parcelas deve estar entre ${elegibilities?.installments.min} e ${elegibilities?.installments.max}.`,
        });
        return false;
      }

      if (
        interestRate &&
        elegibilities?.interestRate?.min &&
        elegibilities?.interestRate?.max &&
        (interestRate < elegibilities?.interestRate.min ||
          interestRate > elegibilities?.interestRate.max)
      ) {
        setElegibilityError({
          message: 'Taxa de juros inválida',
          description: `A taxa de juros deve estar entre ${
            elegibilities?.interestRate.min * 100
          }% e ${elegibilities?.interestRate.max * 100}%.`,
        });
        return false;
      }

      if (
        amountValue &&
        elegibilities?.operationValue?.min &&
        elegibilities?.operationValue?.max &&
        (amountValue > elegibilities?.operationValue?.max ||
          amountValue < elegibilities?.operationValue?.min)
      ) {
        setElegibilityError({
          message: 'Valor de operação inválido',
          description: `O valor da operação deve estar entre ${formatCurrency(
            elegibilities?.operationValue?.min,
          )} e ${formatCurrency(elegibilities?.operationValue?.max)}.`,
        });
        return false;
      }

      return true;
    },
    [elegibilities],
  );

  const simulate = async (
    productId: string,
    parameters: TSimulatorV2Request,
    options?: { useNotificationOnly?: boolean },
  ): Promise<TSimulatorV2Response | null> => {
    const isValid = validateElegibility(parameters);

    if (!isValid) {
      setFirstPaymentDateError(false);
      return null;
    }

    try {
      setSimulationLoading(true);
      setFirstPaymentDateError(false);
      const simulatorService = new SimulatorV2HttpService();
      const res = await simulatorService.simulate(
        productId,
        parameters,
        options,
      );
      setFirstPaymentDateError(false);
      return res.data;
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: ApiErrorBody };
      };
      const errorData = axiosError?.response?.data;
      const rawMessage = errorData?.message;
      const incompatibilityMessage =
        'Incompatibility between the first installment due date and registration date';

      if (
        typeof rawMessage === 'string' &&
        rawMessage.includes(incompatibilityMessage)
      ) {
        setFirstPaymentDateError(true);
      }

      if (options?.useNotificationOnly) {
        const message = getApiErrorMessage(errorData, {
          translateMessage: true,
        });
        showSnackbar(message, 'error', 100000);
      }
      setSimulationLoading(false);
      return null;
    } finally {
      setSimulationLoading(false);
    }
  };

  const downloadSimulation = async (id: string) => {
    try {
      setSimulationLoading(true);
      const simulatorHttpService = new SimulatorV2HttpService();
      simulatorHttpService.setRequestConfig({
        responseType: 'arraybuffer',
      });
      const response = await simulatorHttpService.getDownloadSimulation(id);

      return response;
    } catch (error) {
      console.error('Error downloadSimulation', error);
    } finally {
      setSimulationLoading(false);
    }
  };

  return {
    simulate,
    simulationLoading,
    downloadSimulation,
    firstPaymentDateError,
    setFirstPaymentDateError,
  };
};

export default useCreditSimulatorV2;
